/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { describerResultToPorts } from "./ports.js";
import { describeInput, describeOutput } from "./schemas.js";
export { VirtualNode };
function discernType(id) {
    const [type] = id.split("-");
    if (["fetch", "secrets", "invoke", "output"].includes(type)) {
        return type;
    }
    return "unknown";
}
/**
 * A virtual node represents a node in a virtual graph. Virtual
 * Graphs aren't actually graphs, but rather code that
 * behaves as in a "graph-like" way: it may invoke components,
 * but its topology is entirely imperative.
 * For example, the runModule code is a virtual graph, and
 * every capability it invokes shows up as a virtual node.
 */
class VirtualNode {
    #id;
    // Currently matches runModule capabilities.
    #type;
    descriptor;
    constructor(descriptor) {
        const { id = "", type, metadata } = descriptor;
        this.#id = id;
        this.#type = type || discernType(id);
        this.descriptor = {
            id,
            type: this.#type,
            metadata,
            configuration: descriptor.configuration,
        };
    }
    title() {
        return this.descriptor.metadata?.title || this.#type;
    }
    description() {
        return this.descriptor.metadata?.description || this.title();
    }
    incoming() {
        return [];
    }
    outgoing() {
        return [];
    }
    isEntry() {
        return false;
    }
    isExit() {
        return false;
    }
    #ports(inputs, outputs) {
        const { type, configuration } = this.descriptor;
        if (type === "output") {
            return describerResultToPorts(this, describeOutput({
                inputs: { ...configuration, ...inputs },
            }), false, inputs);
        }
        else if (type === "input") {
            return describerResultToPorts(this, describeInput({
                inputs: { ...configuration, ...inputs },
            }), false, inputs, outputs);
        }
        else {
            return {
                inputs: {
                    ports: [],
                    fixed: true,
                },
                outputs: {
                    ports: [],
                    fixed: true,
                },
                side: {
                    ports: [],
                    fixed: true,
                },
                updating: false,
            };
        }
    }
    type() {
        return {
            metadata: async () => {
                return {
                    title: this.#type,
                };
            },
            currentMetadata: () => {
                return {
                    title: this.#type,
                };
            },
            type: () => {
                return this.#type;
            },
            ports: async () => {
                return this.#ports({}, {});
            },
        };
    }
    async describe() {
        return {
            inputSchema: {},
            outputSchema: {},
        };
    }
    configuration() {
        return this.descriptor.configuration || {};
    }
    metadata() {
        return this.descriptor.metadata || {};
    }
    async ports(inputs, outputs) {
        return this.#ports(inputs, outputs);
    }
    currentPorts(inputs, outputs) {
        return this.#ports(inputs, outputs);
    }
    deleted() {
        return false;
    }
}
//# sourceMappingURL=virtual-node.js.map